import { Edit as EditIcon } from "@mui/icons-material";
import { Avatar, Badge, IconButton, styled } from "@mui/material";
import { type ChangeEvent, useId } from "react";

const HiddenInput = styled("input")({ display: "none" });

export type EntityType = "room" | "school" | "district" | "organization" | "event";

export interface EntityAvatarProps {
  entityName: string;
  imageUrl?: string;
  /**
   * `alt` for the underlying image. Pass `""` when the entity name is already shown as adjacent text
   * (avoids duplicate announcement / axe `image-redundant-alt`). Defaults to `entityName`.
   */
  imgAlt?: string;
  canEdit: boolean;
  size?: "small" | "medium" | "large" | "xlarge";
  className?: string;
  status?: "idle" | "loading" | "error" | "success";
  onImageChange?: (file: File) => void;
}

const getSizeStyles = (size: EntityAvatarProps["size"]) => {
  const sizeMap = {
    large: { fontSize: "2rem", height: "4em", width: "4em" },
    medium: { fontSize: "1.5rem", height: "3em", width: "3em" },
    small: { fontSize: "1rem", height: "2em", width: "2em" },
    xlarge: { fontSize: "4rem", height: "3em", width: "3em" },
  };
  return sizeMap[size || "medium"];
};

const getIconButtonStyles = (size: EntityAvatarProps["size"]) => {
  const sizeMap = {
    large: { height: 32, padding: "4px", width: 32 },
    medium: { height: 24, padding: "3px", width: 24 },
    small: { height: 20, padding: "2px", width: 20 },
    xlarge: { height: 40, padding: "4px", width: 40 },
  };
  return sizeMap[size || "medium"];
};

const getIconStyles = (size: EntityAvatarProps["size"]) => {
  const sizeMap = {
    large: { fontSize: "1rem" },
    medium: { fontSize: "0.875rem" },
    small: { fontSize: "0.75rem" },
    xlarge: { fontSize: "1.5rem" },
  };
  return sizeMap[size || "medium"];
};

export const EntityAvatar = ({
  entityName,
  imageUrl,
  imgAlt,
  canEdit,
  size = "medium",
  className,
  status = "idle",
  onImageChange,
}: EntityAvatarProps) => {
  const inputId = useId();
  const altText = imgAlt !== undefined ? imgAlt : entityName;
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files?.length === 1 && onImageChange) {
      onImageChange(event.target.files[0]);
    }
  };

  const avatar = (
    <Avatar
      key={imageUrl}
      alt={altText}
      src={imageUrl}
      sx={{
        ...getSizeStyles(size),
      }}
      className={className}
    />
  );

  if (!canEdit) {
    return avatar;
  }

  return (
    <label data-testid="EntityAvatar" htmlFor={inputId} title="Click to upload a new image">
      <HiddenInput accept="image/*" id={inputId} onChange={handleChange} type="file" disabled={status === "loading"} />
      <Badge
        anchorOrigin={{ horizontal: "left", vertical: "bottom" }} // Changed to bottom-left
        badgeContent={
          <IconButton
            aria-label={`Edit picture`}
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              document.getElementById(inputId)?.click();
            }}
            disabled={status === "loading"}
            sx={{
              "&:focus-visible": {
                outline: (theme) => `2px solid ${theme.palette.primary.main}`,
                outlineOffset: "2px",
              },
              "&:hover": {
                backgroundColor: "primary.light",
              },
              backgroundColor: "primary.main",
              border: "2px solid",
              borderColor: "primary.main",
              color: "white",
              ...getIconButtonStyles(size),
            }}
          >
            <EditIcon sx={getIconStyles(size)} />
          </IconButton>
        }
        data-testid="edit-picture-button"
        overlap="circular"
      >
        {avatar}
      </Badge>
    </label>
  );
};
